import React, { useImperativeHandle, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText } from '@mui/material';
import { useSettingsStore } from '@core/store';
import { useTranslation } from 'react-i18next';
import { WcnButton } from '@/components';

export interface ConfirmationDialogProps {
    // Content props
    heading?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;

    // Control props
    isPrimaryButtonDisabled?: boolean;

    // Event handlers
    onConfirm?: () => void;
    onClose?: () => void;
}

export interface ConfirmationDialogRef {
    openDialog: () => void;
}

type Props = ConfirmationDialogProps & {
    dialogRef?: React.Ref<ConfirmationDialogRef>;
};

const ConfirmationDialog = ({
    // Content props
    heading = '',
    message = '',
    confirmText = '',
    cancelText = '',

    // Control props
    isPrimaryButtonDisabled = false,

    // Event handlers
    onConfirm,
    onClose,

    // Ref prop
    dialogRef
}: Props) => {
    const [open, setOpen] = useState(false);
    const { darkMode, isMobile } = useSettingsStore();
    const { t } = useTranslation();

    useImperativeHandle(dialogRef, () => ({
        openDialog: () => setOpen(true)
    }));

    const handleConfirm = () => {
        onConfirm?.();
        setOpen(false);
    };

    const handleClose = () => {
        onClose?.();
        setOpen(false);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth={isMobile}
            fullScreen={false}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    minWidth: isMobile ? 'auto' : '400px',
                    maxWidth: '500px',
                    margin: isMobile ? 1 : 3,
                    backgroundColor: darkMode ? '#444444' : '#ffffff',
                    color: darkMode ? '#ffffff' : '#000000'
                }
            }}
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.5)'
                }
            }}
        >
            <DialogTitle
                sx={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: darkMode ? '#ffffff' : '#000000',
                    paddingBottom: 1
                }}
            >
                {heading}
            </DialogTitle>

            <DialogContent
                sx={{
                    paddingTop: '8px !important',
                    paddingBottom: 2
                }}
            >
                <DialogContentText
                    sx={{
                        color: darkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)',
                        fontSize: '1rem',
                        lineHeight: 1.5,
                        whiteSpace: 'pre-line'
                    }}
                >
                    {message}
                </DialogContentText>
            </DialogContent>

            <DialogActions
                sx={{
                    padding: '16px 24px',
                    gap: 1,
                    justifyContent: 'flex-end'
                }}
            >
                <WcnButton onClick={handleClose} variant="red">
                    {cancelText ? t(cancelText) : t('no')}
                </WcnButton>

                <WcnButton onClick={handleConfirm} disabled={isPrimaryButtonDisabled} variant="primary">
                    {confirmText ? t(confirmText) : t('yes')}
                </WcnButton>
            </DialogActions>
        </Dialog>
    );
};

ConfirmationDialog.displayName = 'ConfirmationDialog';

export default ConfirmationDialog;
